import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { VehiclePricing } from '@/components/tourist-tours/TourVehiclePricing';

export interface TourVehiclePricingFromDB {
  id: string;
  tour_id: string;
  vehicle_type_id: string;
  price: number;
  currency: string;
  vehicle_types: {
    name_ar: string;
  } | null;
}

const fetchTourVehiclePricing = async (tourId: string): Promise<TourVehiclePricingFromDB[]> => {
  const { data, error } = await supabase
    .from('tour_vehicle_pricing')
    .select(`
      id,
      tour_id,
      vehicle_type_id,
      price,
      currency,
      vehicle_types(name_ar)
    `)
    .eq('tour_id', tourId)
    .order('price', { ascending: true });
  
  if (error) throw new Error(error.message);
  return data as TourVehiclePricingFromDB[];
};

const saveTourVehiclePricing = async (tourId: string, pricing: VehiclePricing[]) => {
  // حذف الأسعار الموجودة
  const { error: deleteError } = await supabase
    .from('tour_vehicle_pricing')
    .delete()
    .eq('tour_id', tourId);

  if (deleteError) throw new Error(deleteError.message);

  // إدراج الأسعار الجديدة
  if (pricing.length > 0) {
    const newPricing = pricing.map(p => ({
      tour_id: tourId,
      vehicle_type_id: p.vehicle_type_id,
      price: p.price,
      currency: p.currency
    }));

    const { error: insertError } = await supabase
      .from('tour_vehicle_pricing')
      .insert(newPricing);

    if (insertError) throw new Error(insertError.message);
  }
};

export const useTourVehiclePricing = (tourId: string | null) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['tour-vehicle-pricing', tourId],
    queryFn: () => fetchTourVehiclePricing(tourId!),
    enabled: !!tourId,
  });

  const saveMutation = useMutation({
    mutationFn: ({ tourId, pricing }: { tourId: string; pricing: VehiclePricing[] }) =>
      saveTourVehiclePricing(tourId, pricing),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tour-vehicle-pricing', tourId] });
      queryClient.invalidateQueries({ queryKey: ['tourist-tours'] });
    },
  });

  return {
    ...query,
    savePricing: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
  };
};